import _ from 'lodash';

import {
    extractLines, isAminoAcidSequence, isCcdCode, isDnaSequence,
    isFasta, isRnaSequence, isSmilesLine, isTargetSequence,
} from 'BMapsSrc/util/mol_format_utils';
import { getCCDSmiles } from 'BMapsSrc/integrations/rcsb';

/**
 * FoldingPrograms
 * @typedef {'Chai-1' | 'Boltz-1' | 'Boltz-2' | 'RosettaFold' | 'AlphaFold'} FoldingProgramId
 * FoldingProgramInfo
 * @typedef {{
 *     foldingProgramId: FoldingProgramId,
 *     label: string,
 *     disabled?: boolean
 *     extractFoldingArgs: Function,
 *     appSpecificLabel: string,
 *     documentationUrl: string,
 *     useLigands: boolean,
 *     allowCPU?: boolean,
 *     allowProteinPrep: boolean,
 *     useAffinity?: boolean,
 *     supportsCCD?: boolean
 * }} FoldingProgramInfo
 * InputSequenceItem - folding input individual item (with optional count), a la GUI.
 * @typedef {{ type: string, data: string, count: number }} InputSequenceItem
 * FoldingInputData - entire folding input coming from the GUI.
 * @typedef {{
 *     foldingProgramId: FoldingProgramId,
 *     sequences: [InputSequenceItem],
 *     useCPU: boolean,
 *     useProteinPrep: boolean,
 *     useApplicationSpecific: boolean,
 *     applicationSpecificInput: string?,
 *     affinityIdentifier?: string,
 * }} FoldingInputData
 * FoldingServiceArgs - arguments sent over the wire to the folding service.
 * @typedef {{
 *     foldingProgramId: string,
 *     foldingArgs: FoldingArgsBoltz | FoldingArgsChai,
 * }} FoldingServiceArgs
 * FoldingArgsBoltz - arguments for Boltz folding service.
 * @typedef {{ foldingProgramId: "Boltz-1" | "Boltz-2", use_protein_prep: boolean?, boltz_args: {
 *     use_cpu: boolean,
 *     fasta?: string,
 *     yaml?: string,
 *     json?: {
 *         sequences: Array<{
 *             protein?: { id: string, sequence: string },
 *             rna?: { id: string, sequence: string },
 *             dna?: { id: string, sequence: string }
 *             ligand?: { id: string, smiles: string },
 *             ccd?: { id: string, ccd: string },
 *          }>
 *      }
 * }}} FoldingArgsBoltz
 * FoldingArgsChai - arguments for Chai folding service.
 * @typedef {{ foldingProgramId: "Chai-1", use_protein_prep: boolean?, chai_args: {
 *     use_cpu: boolean,
 *     fasta?: string,
 * }}} FoldingArgsChai
 * FoldingResults - results from the folding service, typically a mmCIF data.
 * @typedef {{ data: string, format: string, errors?: string[] }} FoldingResults
 */

/** @type {FoldingProgramId} */
export const defaultFoldingProgramId = 'Boltz-2';
// Constants
/** @type {[FoldingProgramInfo]} */
export const foldingPrograms = [
    {
        foldingProgramId: 'Boltz-1',
        label: 'Boltz-1',
        extractFoldingArgs: extractFoldingArgsBoltz,
        useLigands: true,
        allowProteinPrep: false,
        allowCPU: false,
        appSpecificLabel: 'Use Exact FASTA / YAML for Boltz-1',
        documentationUrl: 'https://github.com/jwohlwend/boltz',
        supportsCCD: true,
    },
    {
        foldingProgramId: 'Boltz-2',
        label: 'Boltz-2',
        extractFoldingArgs: extractFoldingArgsBoltz,
        useLigands: true,
        allowProteinPrep: false,
        allowCPU: false,
        appSpecificLabel: 'Use Exact FASTA / YAML for Boltz-2',
        documentationUrl: 'https://github.com/jwohlwend/boltz',
        useAffinity: true,
        supportsCCD: true,
    },
    {
        foldingProgramId: 'Chai-1',
        label: 'Chai-1',
        extractFoldingArgs: extractFoldingArgsChai,
        useLigands: true,
        allowCPU: false,
        appSpecificLabel: 'Use Exact FASTA for Chai-1',
        documentationUrl: 'https://github.com/chaidiscovery/chai-lab',
    },
    {
        foldingProgramId: 'RosettaFold',
        label: 'RoseTTAFold All-Atom',
        disabled: true,
    },
    {
        foldingProgramId: 'Alphafold',
        label: 'AlphaFold 3',
        disabled: true,
    },
];

export function getFoldingProgramInfo(foldingProgramId) {
    const found = foldingPrograms.find((fp) => fp.foldingProgramId === foldingProgramId);
    return found && _.cloneDeep(found);
}

export const foldingSequenceTypes = {
    protein: {
        id: 'protein',
        label: 'Protein Sequence',
        unit: 'Protein Sequence',
        unitPlural: 'Protein Sequences',
        title: 'Add each input line as a protein amino acid sequence',
        placeholder: 'protein sequences',
        filetypes: ['fasta'],
    },
    dna: {
        id: 'dna',
        label: 'DNA Sequence',
        unit: 'DNA Sequence',
        unitPlural: 'DNA Sequences',
        title: 'Add each input line as a DNA sequence',
        placeholder: 'DNA sequences',
        filetypes: ['fasta'],
    },
    rna: {
        id: 'rna',
        label: 'RNA Sequence',
        unit: 'RNA Sequence',
        unitPlural: 'RNA Sequences',
        title: 'Add each input line as an RNA sequence',
        placeholder: 'RNA sequences',
        filetypes: ['fasta'],
    },
    ligand: {
        id: 'ligand',
        label: 'Ligand (Smiles)',
        unit: 'Ligand',
        unitPlural: 'Ligands',
        title: 'Add each input line as a ligand by smiles string',
        placeholder: 'ligand smiles',
        filetypes: ['.smi'],
    },
    // Enabling ccd codes for managed input
    // For programs that don't support ccd we can use api call
    ccd: {
        id: 'ccd',
        label: 'Ligand (CCD code)',
        unit: 'Ligand',
        unitPlural: 'Ligands',
        title: 'Add ligand(s) by CCD code to folding input',
        placeholder: 'Ligand CCD codes',
        filetypes: [],
    },
    mixed: {
        id: 'mixed',
        label: 'Mixed (Detect Types from Text)',
        unit: 'Component',
        unitPlural: 'Components',
        title: 'Add detected data types to folding input',
        placeholder: 'sequences and/or ligand smiles',
        filetypes: ['fasta', '.smi'],
    },
};

const exampleData = [
    {
        name: 'Boltz Sample',
        foldingProgramId: 'Boltz-2',
        sequences: [
            {
                type: 'protein',
                data: 'MAEPDPSHPLETQAGKVQEAQDSDSDSEGGAAGGEADMDFLRNLFSQTLSLGSQKERLLDELTLEGVARYMQSERCRRVICLVGAGISTSAGIPDFRSPSTGLYDNLEKYHLPYPEAIFEISYFKKHPEPFFALAKELYPGQFKPTICHYFMRLLKDKGLLLRCYTQNIDTLERIAGLEQEDLVEAHGTFYTSHCVSASCRHEYPLSWMKEKIFSEVTPKCEDCQSLVKPDIVFFGESLPARFFSCMQSDFLKVDLLLVMGTSLQVQPFASLISKAPLSTPRLLINKEKAGQSDPFLGMIMGLGGGMDFDSKKAYRDVAWLGECDQGCLALAELLGWKKELEDLVRREHASIDAQSGAGVPNPSTSASPKKSPPPAKDEARTTEREKPQ',
                count: 1,
            },
            {
                type: 'ccd',
                data: 'ZN',
                count: 1,
            },
            {
                type: 'ligand',
                data: 'Cc1cn(c(n1)C)c2ccc3c(c2)CN(CC3)c4ncc(s4)Cc5ccc(cc5)n6cccn6',
                count: 1,
            },
        ],
        affinityIdentifier: 'Cc1cn(c(n1)C)c2ccc3c(c2)CN(CC3)c4ncc(s4)Cc5ccc(cc5)n6cccn6',
    },
    {
        name: 'Chai-1 Sample',
        foldingProgramId: 'Chai-1',
        sequences: [
            {
                type: 'protein',
                data: 'AGSHSMRYFSTSVSRPGRGEPRFIAVGYVDDTQFVRFDSDAASPRGEPRAPWVEQEGPEYWDRETQKYKRQAQTDRVSLRNLRGYYNQSEAGSHTLQWMFGCDLGPDGRLLRGYDQSAYDGKDYIALNEDLRSWTAADTAAQITQRKWEAAREAEQRRAYLEGTCVEWLRRYLENGKETLQRAEHPKTHVTHHPVSDHEATLRCWALGFYPAEITLTWQWDGEDQTQDTELVETRPAGDGTFQKWAAVVVPSGEEQRYTCHVQHEGLPEPLTLRWEP',
                count: 1,
            },
            {
                type: 'protein',
                data: 'AIQRTPKIQVYSRHPAENGKSNFLNCYVSGFHPSDIEVDLLKNGERIEKVEHSDLSFSKDWSFYLLYYTEFTPTEKDEYACRVNHVTLSQPKIVKWDRDM',
                count: 1,
            },
            {
                type: 'protein',
                data: 'GAAL',
                count: 1,
            },

            {
                type: 'ligand',
                data: 'CCCCCCCCCCCCCC(=O)O',
                count: 1,
            },
        ],
    },
];

export const getExampleData = () => _.cloneDeep(exampleData);

/**
 * Given folding input and a folding program id, prepare the input to send to the folding program.
 * @param {FoldingInputData} foldingInputData
 * @returns {{ foldingRequestArgs: FoldingServiceArgs?, loadOptions: object?, errors: string[]? }}
 */
export async function prepareFoldingArgs(foldingInputData) {
    const foldingProgramId = foldingInputData.foldingProgramId;
    const foldingProgram = getFoldingProgramInfo(foldingProgramId);
    const {
        sequences: updatedSequences, errors,
    } = await evaluateCCDSequences(foldingInputData.sequences, foldingProgram);
    if (errors.length > 0) {
        return { errors };
    }
    const expandedSequences = fullSequenceList(updatedSequences);
    const foldingArgs = foldingProgram.extractFoldingArgs({
        ...foldingInputData,
        sequences: expandedSequences, // override with expanded, smiles-annotated sequence list
    });
    const loadOptions = {
        ligandSmiles: expandedSequences
            .filter((seq) => seq.type === 'ligand' || seq.type === 'ccd')
            .map((seq) => seq.smiles),
        preserveHydrogens: !!foldingInputData.useProteinPrep,
    };
    return { foldingRequestArgs: { foldingProgramId, foldingArgs }, loadOptions };
}

// Processing folding input

/**
 * Extract sequences from a text input, with reasonable cleverness to account for user input.
 *
 * 1. General case: sequences and ligands are extracted one per line
 * 2. If there are FASTA header markers >, enter FASTA mode, joining sequence lines between headers
 * 3. Leave FASTA mode if:
 *    - The next line would cause the current sequence to not be a valid seq type (eg smiles)
 *    - A smiles line is followed by more data instead of a FASTA header (smiles can't be multiline)
 *
 * See unit test in mol_format_utils.test.js
 * @param {string} input
 * @returns {string[]}
 */
export function extractSequences(input) {
    const lines = extractLines(input, /^;/); // Ignore FASTA comments starting with ;
    const sequences = [];
    let inFasta = false;
    let currentSequence = '';
    function grabCurrentSequence() {
        if (currentSequence) {
            sequences.push(currentSequence);
            currentSequence = '';
        }
    }
    for (const line of lines) {
        if (line.startsWith('>')) {
            inFasta = true;
            grabCurrentSequence();
        } else if (inFasta) {
            if (!currentSequence) {
                currentSequence = line;
                continue;
            }
            // Multi-line entries are only allowed for sequence types, not ligands
            if (isTargetSequence(currentSequence) && isTargetSequence(currentSequence + line)) {
                currentSequence += line;
            } else {
                // Leave FASTA mode: either multiline ligand or incoming line breaks currentSequence
                inFasta = false;
                grabCurrentSequence();
                sequences.push(line);
            }
        } else {
            // Just adding line-by-line
            sequences.push(line);
        }
    }
    grabCurrentSequence();
    return sequences;
}

export function guessSequenceTypeMulti(input) {
    const sequences = extractSequences(input);
    const result = sequences.reduce((acc, line) => {
        const type = guessSequenceTypeOne(line);
        if (acc == null) return type;
        if (acc === type) return acc;
        // If we have a mix of types, return nothing
        return 'mixed';
    }, null);
    return result;
}

export function guessSequenceTypeOne(input) {
    const str = input?.trim() || '';
    switch (true) {
        // Disable CCD codes for managed input.
        // For Boltz, can use the app-specific FASTA / yaml
        case isCcdCode(str): return 'ccd';
        case isDnaSequence(str): return 'dna';
        case isRnaSequence(str): return 'rna';
        case isAminoAcidSequence(str): return 'protein';
        case isSmilesLine(str): return 'ligand';
        default: return '';
    }
}

export function checkSequenceType(sequence, type) {
    switch (type) {
        case 'protein': return isAminoAcidSequence(sequence);
        case 'dna': return isDnaSequence(sequence);
        case 'rna': return isRnaSequence(sequence);
        case 'ligand': return isSmilesLine(sequence);
        case 'ccd': return isCcdCode(sequence);
        default: return false;
    }
}

// Preparing data for folding programs

/**
 * @param {FoldingInputData} foldingInputData
 * @returns {FoldingArgsBoltz}
 */
export function extractFoldingArgsBoltz(foldingInputData) {
    const {
        foldingProgramId,
        sequences,
        useCPU,
        useProteinPrep,
        useApplicationSpecific,
        applicationSpecificInput,
        affinityIdentifier,
    } = foldingInputData;
    const { useAffinity } = getFoldingProgramInfo(foldingProgramId);

    const boltzArgs = { use_cpu: useCPU };
    if (useApplicationSpecific && applicationSpecificInput) {
        const data = applicationSpecificInput;
        // Boltz can use FASTA or YAML formats
        if (isFasta(data)) {
            boltzArgs.fasta = data;
        } else { // assume valid yaml if not FASTA
            boltzArgs.yaml = data;
        }
    } else {
        boltzArgs.json = {
            sequences: sequences.map((seq, seqI) => formatBoltzItemJson(seq, seqI)),
        };
    }

    if (useAffinity && affinityIdentifier && !useApplicationSpecific) {
        attachBoltzAffinityBinder(boltzArgs, affinityIdentifier);
    }

    return {
        foldingProgramId,
        boltz_args: boltzArgs,
        use_protein_prep: useProteinPrep,
    };
}

function attachBoltzAffinityBinder(boltzArgs, affinityIdentifier) {
    let affinityBinder = null;
    if (!boltzArgs.json?.sequences) return;
    for (const seq of boltzArgs.json.sequences) {
        if (seq.ligand?.smiles === affinityIdentifier) {
            affinityBinder = seq.ligand.id;
            break;
        }
        if (seq.ligand?.ccd === affinityIdentifier) {
            affinityBinder = seq.ligand.id;
            break;
        }
    }
    if (affinityBinder) {
        boltzArgs.json.properties = [{ affinity: { binder: affinityBinder } }];
    }
}

/**
 * @param {InputSequenceItem} sequence
 */
export function formatBoltzItemJson(sequence, index) {
    const id = getChainForIndex(index);
    switch (sequence.type) {
        case 'protein':
            return { protein: { id, sequence: sequence.data } };
        case 'ligand':
            return { ligand: { id, smiles: sequence.data } };
        case 'ccd':
            return { ligand: { id, ccd: sequence.data } };
        case 'rna':
            return { rna: { id, sequence: sequence.data } };
        case 'dna':
            return { dna: { id, sequence: sequence.data } };
        default:
            return sequence.data;
    }
}

export function formatBoltzItemFasta(sequence, index) { // eslint-disable-line no-unused-vars
    const id = getChainForIndex(index);
    switch (sequence.type) {
        case 'protein':
            return `>${id}|protein\n${sequence.data}`;
        case 'ligand':
            return `>${id}|smiles\n${sequence.data}`;
        case 'ccd':
            return `>${id}|ccd\n${sequence.data}`;
        case 'rna':
            return `>${id}|rna\n${sequence.data}`;
        case 'dna':
            return `>${id}|dna\n${sequence.data}`;
        default:
            return sequence.data;
    }
}

/**
 * @param {FoldingInputData} foldingInputData
 * @returns {FoldingArgsChai}
 */
export function extractFoldingArgsChai(foldingInputData) {
    const {
        foldingProgramId,
        sequences,
        useCPU,
        useProteinPrep,
        useApplicationSpecific,
        applicationSpecificInput,
    } = foldingInputData;

    const chaiArgs = { use_cpu: useCPU };

    if (useApplicationSpecific && !!applicationSpecificInput) {
        chaiArgs.fasta = applicationSpecificInput;
    } else {
        chaiArgs.fasta = sequences
            .map((seq, seqI) => formatChaiItemFasta(seq, seqI))
            .join('\n');
    }

    return {
        foldingProgramId,
        chai_args: chaiArgs,
        use_protein_prep: useProteinPrep,
    };
}

export function formatChaiItemFasta(sequence, index) {
    const id = getChainForIndex(index);
    switch (sequence.type) {
        case 'protein':
            return `>protein|name=${id}\n${sequence.data}`;
        case 'ligand':
            return `>ligand|name=${id}\n${sequence.data}`;
        case 'ccd':
            return `>ligand|name=${id}\n${sequence.smiles}`;
        case 'rna':
            return `>rna|name=${id}\n${sequence.data}`;
        case 'dna':
            return `>dna|name=${id}\n${sequence.data}`;
        default:
            return sequence.data;
    }
}

/**
 * @param {InputSequenceItem[]} sequences
 */
export function fullSequenceList(sequences) {
    return sequences.flatMap((seq) => Array(seq.count).fill(seq));
}

/**
 * @param {FoldingInputData} formValues
 */
export async function evaluateCCDSequences(originalSequences, foldingProgram) {
    const supportsCCD = foldingProgram.supportsCCD;
    const sequences = [];
    const errors = [];
    for (const sequence of originalSequences) {
        switch (sequence.type) {
            case 'ligand':
                sequences.push({ ...sequence, smiles: sequence.data });
                break;
            case 'ccd': {
                const smilesResult = await getCCDSmiles(sequence.data);
                if (smilesResult.errors && smilesResult.errors.length > 0) {
                    errors.push(...smilesResult.errors);
                    continue;
                }
                sequences.push({
                    ...sequence,
                    type: supportsCCD ? sequence.type : 'ligand',
                    data: supportsCCD ? sequence.data : smilesResult.smiles,
                    smiles: smilesResult.smiles,
                });
                break;
            }
            default:
                sequences.push(sequence);
        }
    }
    return { sequences, errors };
}

/**
 * A function to convert an index into an string ID, kind of like Excel columns:
 * A, B, C, ... Y, Z, AA, AB, ..., ZZ, AAA, AAB, ... ZZZ, AAAA, ...
 * The default character set is A-Za-z0-9.
 * @param {number} index
 * @param {string} chars
 * @returns {string}
 */
export function getChainForIndex(
    index,
    chars='ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
) {
    const len = chars.length;

    // Implementation:
    // This is based on the understanding that:
    // * the first len indices have 1 character (group 1)
    // * the next len*len indices have 2 characters (group 2)
    // * the next len**3 indices have 3 characters (group 3)
    // First, find the start and end indices for the group that target index falls into
    const bounds = [0, len];
    while (bounds[bounds.length-1] <= index) {
        bounds.push((len ** bounds.length) + bounds[bounds.length-1]);
    }
    const groupStart = bounds[bounds.length-2];
    const groupEnd = bounds[bounds.length-1];

    // With the start and end, we now have an offset to more easily assign the letters.
    // We're essentially going to 0 for the set of ids that has this many letters.
    const start = index - groupStart;
    const end = groupEnd - groupStart;
    const rs = [];
    for (let i = 1, working = start; i < end; i *= len) {
        // working works down from the modified index, grabbing the remainders used to get the IDs
        // i works up, used to determine the end condition.
        const remainder = working % len;
        const div = Math.floor(working / len);
        rs.unshift(remainder);
        working = div;
    }
    const chain = rs.map((r) => chars[r]).join('');

    // Naive implementation, march through number line, updating letters, collect in reverse order.
    // const places = [0];
    // let i = 0;
    // while (i < index) {
    //     const pos = (places[0])++;
    //     const char = chars[pos];
    //     for (const [pI, pPos] of places.entries()) {
    //         if (pPos >= len) {
    //             places[pI] = 0;
    //             if (places[pI+1] == null) places[pI+1] = 0;
    //             else (places[pI+1])++;
    //         }
    //     }
    //     i++;
    // }
    // const chain = places.reverse().map(r => chars[r]).join('');
    return chain;
}
